import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Phone, MessageCircle, Headphones, AudioLines } from 'lucide-react';

const ICON_MAP = {
  mic: Mic,
  phone: Phone,
  message: MessageCircle,
  headphones: Headphones,
  waveform: AudioLines,
};

/**
 * Adjust a hex color's lightness by `delta` (in HSL space, -100..+100).
 * Returns a hex string. Used to derive a darker rim shadow + slightly
 * lighter latitude lines from the orb base color — guarantees the orb
 * stays in a single color family (no more hardcoded warm browns).
 */
function shadeColor(hex, delta) {
  if (!hex || typeof hex !== 'string') return hex;
  const m = hex.replace('#', '');
  if (m.length !== 6) return hex;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  const newL = Math.max(0, Math.min(1, l + delta / 100));
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let nr; let ng; let nb;
  if (s === 0) {
    nr = ng = nb = newL;
  } else {
    const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    const p = 2 * newL - q;
    nr = hue2rgb(p, q, h + 1 / 3);
    ng = hue2rgb(p, q, h);
    nb = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

/**
 * VoxinityOrb — solid 3D sphere with conic-style shading, latitude lines, a
 * lens-flare highlight, and a soft halo. Slowly rotates.
 *
 * Two-color model:
 *   primary   sphere body + halo + auto-derived rim shadow + latitude lines
 *   accent    icon ONLY (no longer bleeds into halo / lens flare)
 *
 * Props:
 *   size         number   px
 *   primary      string   orb body / halo color
 *   accent       string   icon color
 *   isActive     bool     show status badge
 *   isListening  bool
 *   isSpeaking   bool
 *   isThinking   bool
 *   audioLevel   0..1     real-time mic / TTS energy
 *   functionCall string   optional pill above the orb
 */
export default function VoxinityOrb({
  size = 400,
  primary = '#1a5eff',
  accent = '#66ffe0',
  icon = 'mic',
  isActive = false,
  isListening = false,
  isSpeaking = false,
  isThinking = false,
  audioLevel = 0,
  functionCall = null,
}) {
  const al = Math.min(Math.max(audioLevel || 0, 0), 1);
  const IconComponent = ICON_MAP[icon] || Mic;

  // Auto-derived shades from primary — keeps the orb in a single color family.
  const primaryLight = useMemo(() => shadeColor(primary, 18), [primary]);
  const primaryDark = useMemo(() => shadeColor(primary, -32), [primary]);
  const primaryDeepest = useMemo(() => shadeColor(primary, -52), [primary]);

  // Rotation speed depends on state — thinking feels jittery & alive.
  const rotateDuration = isThinking ? 12 : isSpeaking ? 30 : 60;

  // Sphere scale tracks audio level when active.
  const sphereScale = isSpeaking ? 1 + al * 0.04 : isActive ? 1 + al * 0.02 : 1;

  // Halo intensity multiplier — punches up when speaking.
  const haloIntensity = useMemo(() => {
    if (isSpeaking) return 1 + al * 0.5;
    if (isListening) return 0.85;
    if (isThinking) return 1.1;
    return 1;
  }, [isSpeaking, isListening, isThinking, al]);

  // Status text + dot color
  const status = isThinking
    ? { label: 'Processing', dot: '#fbbf24', text: 'text-amber-300' }
    : isSpeaking
    ? { label: 'Speaking', dot: '#34d399', text: 'text-emerald-300' }
    : isListening
    ? { label: 'Listening', dot: '#22d3ee', text: 'text-cyan-300' }
    : { label: 'Standby', dot: '#fb923c', text: 'text-orange-300' };

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
      data-testid="voxinity-orb"
    >
      {/* ─── Halo glow (back layer, blurred radial) — derived from primary only ─── */}
      <motion.div
        className="absolute inset-0 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${primary}55 0%, ${primary}33 35%, ${primary}11 60%, transparent 78%)`,
        }}
        animate={{ opacity: [haloIntensity * 0.75, haloIntensity, haloIntensity * 0.75] }}
        transition={{ duration: isSpeaking ? 1.2 : 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ─── Rotating + breathing sphere ─── */}
      <motion.div
        className="absolute inset-0"
        style={{ transform: 'rotate(-12deg)' }}
        animate={{ rotate: 348 }}
        transition={{ duration: rotateDuration, repeat: Infinity, ease: 'linear' }}
      >
        <motion.svg
          viewBox="-300 -300 600 600"
          className="w-full h-full"
          style={{ overflow: 'visible' }}
          animate={{ scale: [sphereScale * 0.97, sphereScale * 1.03, sphereScale * 0.97] }}
          transition={{
            duration: isSpeaking ? 0.9 : isThinking ? 1.5 : 3.2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <defs>
            {/* Sphere shading: lighter-primary highlight on top, primary mid,
                auto-derived darker primary at the rim — single color family. */}
            <radialGradient id="vox-sphere-shade" cx="38%" cy="32%" r="75%">
              <stop offset="0%" stopColor={primaryLight} stopOpacity="0.95" />
              <stop offset="30%" stopColor={primary} stopOpacity="1" />
              <stop offset="65%" stopColor={primary} stopOpacity="0.9" />
              <stop offset="92%" stopColor={primaryDark} stopOpacity="1" />
              <stop offset="100%" stopColor={primaryDeepest} stopOpacity="1" />
            </radialGradient>

            <radialGradient id="vox-sphere-shadow" cx="70%" cy="70%" r="70%">
              <stop offset="0%" stopColor="#000" stopOpacity="0" />
              <stop offset="60%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
            </radialGradient>

            {/* Lens flare: pure white specular highlight that fades to transparent
                — no longer tinted with accent so the icon stays readable. */}
            <radialGradient id="vox-flare">
              <stop offset="0%" stopColor="#fff" stopOpacity="1" />
              <stop offset="40%" stopColor="#fff" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </radialGradient>

            <clipPath id="vox-sphere-clip">
              <circle cx="0" cy="0" r="170" />
            </clipPath>
          </defs>

          {/* Sphere body */}
          <circle cx="0" cy="0" r="170" fill="url(#vox-sphere-shade)" />
          <circle cx="0" cy="0" r="170" fill="url(#vox-sphere-shadow)" />

          {/* Latitude lines — slightly lighter primary, subtle */}
          <g
            clipPath="url(#vox-sphere-clip)"
            stroke={primaryLight}
            strokeOpacity="0.28"
            strokeWidth="0.6"
            fill="none"
          >
            <ellipse cx="0" cy="0" rx="170" ry="30" />
            <ellipse cx="0" cy="0" rx="170" ry="65" />
            <ellipse cx="0" cy="0" rx="170" ry="100" />
            <ellipse cx="0" cy="0" rx="170" ry="135" />
            <ellipse cx="0" cy="0" rx="170" ry="160" />
          </g>

          {/* Lens flare highlight (brighter while speaking) */}
          <circle
            cx="80"
            cy="-70"
            r={22 + (isSpeaking ? al * 8 : 0)}
            fill="url(#vox-flare)"
            opacity={isSpeaking ? 0.95 : 0.85}
          />
        </motion.svg>
      </motion.div>

      {/* ─── Centered icon — accent color only, no primary outer-glow halo ─── */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
        animate={{
          scale: isSpeaking ? [1, 1.12, 1] : [1, 1.04, 1],
        }}
        transition={{
          duration: isSpeaking ? 0.6 : 2.4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <IconComponent
          size={Math.round(size * 0.11)}
          strokeWidth={1.75}
          style={{
            color: accent,
            filter: `drop-shadow(0 0 ${size * 0.018}px ${accent}cc)`,
          }}
        />
      </motion.div>

      {/* ─── Status badge (only when active) ─── */}
      {isActive && (
        <div
          className="absolute z-20 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          style={{
            bottom: -4,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
          }}
        >
          <motion.div
            className="rounded-full"
            style={{ width: 5, height: 5, backgroundColor: status.dot }}
            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: isThinking ? 0.4 : 1.2, repeat: Infinity }}
          />
          <span className={`text-[10px] font-medium tracking-wide ${status.text}`}>
            {status.label}
          </span>
        </div>
      )}

      {/* ─── Function-call pill above orb ─── */}
      <AnimatePresence>
        {functionCall && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.9 }}
            className="absolute z-20 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{
              top: -8,
              background: 'rgba(139,92,246,0.2)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(139,92,246,0.3)',
            }}
          >
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
            <span className="text-[10px] text-violet-200 font-medium tracking-wide">
              {functionCall}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
